import { FC } from 'react';
import ReactSkeleton from 'react-loading-skeleton';

export type ImageSkeletonProps = {
  /** The image's own width. Omit for a responsive image and the skeleton fills its ancestor. */
  width?: number | string;
  /** The image's own height. Omit for a responsive image and the skeleton fills its ancestor. */
  height?: number | string;
};

/**
 * A shimmer occupying exactly the footprint of the image it stands in for.
 *
 * Dimensions are stated here rather than inherited: a class chain can be broken by an ancestor or
 * overridden by a carousel writing inline styles, leaving the shimmer a different shape than the
 * photo that replaces it. Pass the same `width`/`height` given to `next/image`.
 */
const ImageSkeleton: FC<ImageSkeletonProps> = ({ width, height }) => {
  const hasFixedSize = width !== undefined && height !== undefined;

  return (
    <span
      className={`absolute left-0 top-0 z-10 ${hasFixedSize ? '' : 'size-full'}`.trim()}
      style={hasFixedSize ? { width, height } : undefined}
      data-testid="skeleton"
    >
      {/*
        The library's shimmer is `inline-flex` plus a trailing <br />, so left alone it sits on a
        text baseline and leaves the font's ascent showing along the top edge. `inline` drops the
        <br />; `block` on both its spans drops the line box. `!` beats the library's own display.
      */}
      <ReactSkeleton inline containerClassName="block size-full leading-none" className="!block size-full" />
    </span>
  );
};

export default ImageSkeleton;
